/**
 * <s-variant-media> — Shows the selected variant's image as the lead gallery image.
 *
 * Wraps the product grid so it hears option `change` events from the variant picker
 * (which bubble up from the product form) and survives the picker's partial swaps.
 *
 * On a colour pick, the variant's featured image moves into the first gallery slot and
 * fades in. Nothing scrolls the page, so the shopper stays on the picker; on phones the
 * carousel snaps back to its first slide, which is now the chosen colour. The chosen photo
 * gets `data-active`, which the template styles as a signal-green frame.
 *
 * Required markup:
 *   [data-media-id] on option inputs/<option>s — the variant's featured media id.
 *   [data-product-gallery] > [data-gallery-media="<id>"] — one wrapper per gallery media item.
 */
class SVariantMedia extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('change', this.#onChange, opts);
    // Warm the image on hover/focus so it's ready the moment the shopper picks.
    this.addEventListener('pointerover', this.#onIntent, opts);
    this.addEventListener('focusin', this.#onIntent, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #mediaIdFor(element) {
    if (!(element instanceof HTMLElement)) return '';
    if (element instanceof HTMLSelectElement) return element.selectedOptions?.[0]?.dataset?.mediaId || '';
    const input = element.matches('input') ? element : element.closest('label')?.querySelector('input[data-media-id]');
    return input?.dataset?.mediaId || '';
  }

  #itemFor(mediaId) {
    const gallery = this.querySelector('[data-product-gallery]');
    const item = gallery?.querySelector(`:scope > [data-gallery-media="${CSS.escape(mediaId)}"]`);
    return item ? { gallery, item } : null;
  }

  #onIntent = (event) => {
    const mediaId = this.#mediaIdFor(event.target);
    if (!mediaId) return;
    const found = this.#itemFor(mediaId);
    const img = found?.item.querySelector('img');
    if (img instanceof HTMLImageElement && img.loading === 'lazy') img.loading = 'eager';
  };

  #onChange = (event) => {
    const mediaId = this.#mediaIdFor(event.target);
    if (!mediaId) return;

    const found = this.#itemFor(mediaId);
    if (!found) return;
    const { gallery, item } = found;

    const img = item.querySelector('img');
    if (img instanceof HTMLImageElement) img.loading = 'eager';

    const alreadyFirst = gallery.firstElementChild === item;
    if (!alreadyFirst) gallery.prepend(item);

    // Frame the chosen colour's photo (styled by data-active:after:* utilities in the template).
    for (const other of gallery.querySelectorAll(':scope > [data-gallery-media][data-active]')) {
      if (other !== item) other.removeAttribute('data-active');
    }
    item.setAttribute('data-active', '');

    // Phones: the gallery is a horizontal carousel; jump to its first slide (instant, no scroll animation).
    if (gallery.scrollLeft !== 0) gallery.scrollLeft = 0;

    if (alreadyFirst) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || typeof item.animate !== 'function') return;

    item.animate(
      [{ opacity: 0, transform: 'scale(0.98)' }, { opacity: 1, transform: 'scale(1)' }],
      { duration: 260, easing: 'cubic-bezier(0.2, 0, 0, 1)' }
    );
  };
}
customElements.define('s-variant-media', SVariantMedia);
